
import {getCurrencySymbol} from "../../lib/utils.ts";

const buildHighlightPattern = (term: string): string => {
    const sepClass = "[.,\\s\\u00A0]";
    let pattern = "";

    for (let i = 0; i < term.length; i++) {
        const ch = term[i];
        const next = term[i + 1];

        if (/\d/.test(ch)) {
            pattern += ch;
            if (next && /\d/.test(next)) {
                pattern += `(?:${sepClass}?)`;
            }
        } else if (ch === '.' || ch === ',') {
            pattern += "[.,]";
        } else {
            pattern += ch.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
        }
    }

    return pattern;
};

interface Props {
    value: number,
    colorAid?: boolean,
    locale?: string,
    currency?: string,
    showCurrency?: boolean,
    highlightText?: string
}

export function MoneyLabel({value, colorAid, locale = 'nl-NL', currency, showCurrency = false, highlightText}: Props) {
    const formatAmount = (): string => {
        const absValue = Math.abs(value);
        const sign = value < 0 ? '-' : value > 0 && colorAid ? '+' : '';

        const formattedNumber = absValue.toLocaleString(locale, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        });

        if (showCurrency) {
            if (!currency) {
                throw new Error('Currency must be provided when showCurrency is true');
            }
            const symbol = getCurrencySymbol(currency);
            return `${symbol} ${sign}${formattedNumber}`;
        }

        return `${sign}${formattedNumber}`;
    };

    const formattedValue = formatAmount();

    const renderContent = () => {
        if (!highlightText) return formattedValue;

        const escapedTerm = buildHighlightPattern(highlightText);
        const regex = new RegExp(`(${escapedTerm})`, 'gi');
        const parts = formattedValue.split(regex);
        const matchRegex = new RegExp(`^${escapedTerm}$`, 'i');

        return (
            <>
                {parts.map((part, i) =>
                    matchRegex.test(part) ? (
                        <mark key={i} className="bg-yellow-200 p-0 rounded-sm">{part}</mark>
                    ) : (
                        part
                    )
                )}
            </>
        );
    };

    const content = renderContent();

    if (colorAid) {
        if (value > 0) {
            return (
                <span className="text-green-600">
                    {content}
                </span>
            )
        }

        return (
            <span className="text-red-600">
                {content}
            </span>
        )
    }

    return (
        <span className="">
            {content}
        </span>
    )
}
